import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ApplicationRef, createComponent, EnvironmentInjector } from '@angular/core';
import { Subscription } from 'rxjs';
import { SearchBarComponent } from './components/search-bar/search-bar.component';
import { FacetSectionComponent } from './components/facet-section/facet-section.component';
import { SelectedFiltersComponent } from './components/selected-filters/selected-filters.component';
import { ResultsListComponent } from './components/results-list/results-list.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { SearchService } from './services/search.service';
import { TranslationService } from './services/translation.service';
import { FilterMap } from './models/filter-map.model';
import { SearchResult } from './models/search-result.model';
import { FacetValuesMap } from './models/facet-map.model';
import { SearchResponse } from './models/search-response.model';
import { SelectedFilterChip } from './models/selected-filter-chip.model';
import { createEmptyFacetLabels } from './utils/facet-labels.util';
import { FacetLabels } from './models/facet-labels.model';
import { getFacetLabel } from './utils/facet-label.util';
import { FACET_CONFIGS } from './constants/facet-configs';
import { RESULTS_PER_PAGE } from './constants/solr.constants';
import { decodeHtmlEntities } from './utils/html-decode.util';
import { WIDGET_CONFIG } from './utils/widget-config.util';
import { SearchSuggestion } from './models/search-suggestion.model';
import { tokenizeText } from './utils/text-normalize.util';
import { QuickResult } from './models/quick-result.model';
import { resolveResultLink } from './utils/result-link.util';
import { FACET_VALUE_ICONS } from './constants/facet-value-icons';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { TrendingSearchesComponent } from './components/trending-searches/trending-searches.component';

interface LocationSuggestionDetail extends SearchSuggestion {
  normalizedTokens: string[];
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    SearchBarComponent,
    FacetSectionComponent,
    SelectedFiltersComponent,
    ResultsListComponent,
    PaginationComponent,
    CapitalizePipe,
    TrendingSearchesComponent
  ],
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly widgetConfig = WIDGET_CONFIG;
  private readonly supportedSortOptions = new Set(['score desc', 'nume_str asc', 'nume_str desc']);

  readonly facetConfigs = FACET_CONFIGS;

  get visibleFacetConfigs() {
    return this.facetConfigs.filter(config => !this.isFacetHidden(config.key));
  }

  private isFacetHidden(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.widgetConfig.defaultFilter, key);
  }

  readonly resultsPerPage = RESULTS_PER_PAGE;
  readonly useQuickResultsDropdown = !this.widgetConfig.showFullUiOnLoad;
  readonly fullSearchUrl = this.widgetConfig.fullSearchUrl;
  readonly hideFacets = this.widgetConfig.hideFacets;

  get resultsSummaryText(): string {
    const procedureCount = this.getTypeCount('procedure');
    const institutionCount = this.getTypeCount('institution');
    const informativePageCount = this.getTypeCount('informativePage');

    const primaryParts: string[] = [];
    if (procedureCount > 0) {
      primaryParts.push(this.translation.translate('proceduresCount').replace('{count}', String(procedureCount)));
    }
    if (institutionCount > 0) {
      primaryParts.push(this.translation.translate('institutionsCount').replace('{count}', String(institutionCount)));
    }
    if (informativePageCount > 0) {
      primaryParts.push(this.translation.translate('informativePagesCount').replace('{count}', String(informativePageCount)));
    }

    if (!primaryParts.length) {
      return '';
    }

    if (this.onlineCount <= 0) {
      return this.formatSummaryList(primaryParts);
    }

    if (procedureCount > 0) {
      const [firstPart, ...otherParts] = primaryParts;
      const remainder = [
        this.translation.translate('onlineCount').replace('{count}', String(this.onlineCount)),
        ...otherParts
      ];

      return `${firstPart} ${this.translation.translate('summaryOfWhich')} ${this.formatSummaryList(remainder)}`;
    }

    return this.formatSummaryList([
      this.translation.translate('onlineCount').replace('{count}', String(this.onlineCount)),
      ...primaryParts
    ]);
  }

  get searchPlaceholder(): string {
    // Always use translation for placeholder (ignore config if it's a default value)
    const configText = this.widgetConfig.searchText;
    const defaultValues = ['Search...', 'Caută...', ''];
    if (!configText || defaultValues.includes(configText)) {
      return this.translation.translate('searchPlaceholder');
    }
    return configText;
  }

  query = '';
  filters: FilterMap = {};
  page = 0;

  isUiExpanded = this.widgetConfig.showFullUiOnLoad;
  isMobileView = false;
  showFiltersPanel = !this.widgetConfig.hideFacets;

  results: SearchResult[] = [];
  quickResults: QuickResult[] = [];
  facets: FacetValuesMap = {};
  total = 0;
  onlineCount = 0;
  processingTime: number | null = null;

  loading = false;
  error: string | null = null;
  quickResultsLoading = false;
  currentSort = this.getDefaultSort();
  searchSubmitted = false;

  facetLabels: FacetLabels = createEmptyFacetLabels();

  private searchDebounceId: number | null = null;
  private quickResultDebounceId: number | null = null;
  private currentRequest?: Subscription;
  private currentQuickResultRequest?: Subscription;
  private isRestoringState = false;
  private lastSerializedState = '';
  private readonly filterParamPrefix = 'f.';
  private readonly popStateListener = (event: PopStateEvent) => this.handlePopState(event);
  private pendingQueryStatePush = false;
  locationSuggestions: SearchSuggestion[] = [];
  private locationSuggestionDetails: LocationSuggestionDetail[] = [];
  private readonly resizeListener = () => this.updateViewportMode();

  private topBarSearchElement: HTMLElement | null = null;
  private topBarSearchComponentRef: any = null;

  constructor(
    private readonly searchService: SearchService,
    private readonly translation: TranslationService,
    private readonly appRef: ApplicationRef,
    private readonly injector: EnvironmentInjector
  ) {}

  ngOnInit(): void {
    this.updateViewportMode();
    window.addEventListener('resize', this.resizeListener);
    this.restoreStateFromUrl(true);
    window.addEventListener('popstate', this.popStateListener);

    // Initialize external top bar search if container exists
    this.initTopBarSearch();

    // Set language from URL path (/ro or /en)
    const urlLang = this.searchService.getUrlLanguage();
    this.translation.setLanguage(urlLang || 'ro');

    this.initializeSearch();
    this.updateTopBarSearch();
  }

  private initializeSearch(): void {
    if (this.useQuickResultsDropdown) {
      if (this.query.trim().length >= 2) {
        this.scheduleQuickResults(false);
      } else {
        this.quickResults = [];
      }
    } else if (this.isUiExpanded) {
      this.loadResults();
    }
  }

  ngOnDestroy(): void {
    this.currentRequest?.unsubscribe();
    this.currentQuickResultRequest?.unsubscribe();
    if (this.searchDebounceId !== null) {
      window.clearTimeout(this.searchDebounceId);
    }
    if (this.quickResultDebounceId !== null) {
      window.clearTimeout(this.quickResultDebounceId);
    }
    window.removeEventListener('popstate', this.popStateListener);
    window.removeEventListener('resize', this.resizeListener);
    
    // Cleanup top bar search
    this.destroyTopBarSearch();
  }

  /**
   * Initialize external search bar in the top bar container if it exists.
   * The container should have id="id-search-top-bar-div"
   */
  private initTopBarSearch(): void {
    const container = document.getElementById('id-search-top-bar-div');
    if (!container) {
      console.log('[AppComponent] No external search container found (id-search-top-bar-div)');
      return;
    }

    console.log('[AppComponent] Found external search container, creating search bar');
    
    // Show the container
    container.style.display = 'block';
    
    // Create the search bar component dynamically
    const componentRef = createComponent(SearchBarComponent, {
      environmentInjector: this.injector,
      hostElement: this.createSearchBarHost(container)
    });
    
    // Set initial inputs
    this.updateTopBarComponentInputs(componentRef);
    
    // Subscribe to outputs
    componentRef.instance.queryChange.subscribe((value: string) => {
      this.onQueryChange(value);
      this.updateTopBarSearch();
    });
    componentRef.instance.clear.subscribe(() => {
      this.onClearSearch();
      this.updateTopBarSearch();
    });
    componentRef.instance.resultSelected.subscribe((result: QuickResult) => {
      this.onQuickResultSelected(result);
    });
    componentRef.instance.searchSubmit.subscribe(() => {
      this.onSearchSubmit();
    });
    
    // Attach to Angular's change detection
    this.appRef.attachView(componentRef.hostView);
    
    this.topBarSearchComponentRef = componentRef;
    this.topBarSearchElement = container;
  }

  private createSearchBarHost(container: HTMLElement): HTMLElement {
    const host = document.createElement('div');
    host.className = 'top-bar-search-wrapper';
    container.appendChild(host);
    return host;
  }

  private updateTopBarComponentInputs(componentRef: any): void {
    const instance = componentRef.instance;
    instance.query = this.query;
    instance.placeholder = this.searchPlaceholder;
    instance.clearLabel = this.translate('clearSearch');
    instance.countyLabel = this.translate('county');
    instance.cityLabel = this.translate('city');
    instance.suggestions = this.useQuickResultsDropdown ? [] : this.locationSuggestions;
    instance.useResultDropdown = this.useQuickResultsDropdown;
    instance.resultItems = this.quickResults;
  }

  /**
   * Update the external top bar search bar with current state
   */
  private updateTopBarSearch(): void {
    if (!this.topBarSearchComponentRef) return;
    
    this.updateTopBarComponentInputs(this.topBarSearchComponentRef);
    this.topBarSearchComponentRef.changeDetectorRef.detectChanges();
  }

  private destroyTopBarSearch(): void {
    if (this.topBarSearchComponentRef) {
      this.appRef.detachView(this.topBarSearchComponentRef.hostView);
      this.topBarSearchComponentRef.destroy();
      this.topBarSearchComponentRef = null;
    }
    if (this.topBarSearchElement) {
      const wrapper = this.topBarSearchElement.querySelector('.top-bar-search-wrapper');
      if (wrapper) {
        wrapper.remove();
      }
      this.topBarSearchElement = null;
    }
  }

  get selectedChips(): SelectedFilterChip[] {
    const chips: SelectedFilterChip[] = [];
    const lang = this.translation.getLanguage();
    const locale = (lang === 'ro' || !lang) ? 'ro-RO' : 'en-US';

    Object.entries(this.filters).forEach(([facetKey, values]) => {
      // Skip hidden filters (defaults from widget config)
      if (this.isFacetHidden(facetKey)) {
        return;
      }

      values.forEach(value => {
        const config = this.facetConfigs.find(item => item.key === facetKey);
        const chipConfig = config?.chip;

        // Determine icon: strict lookup first, then general config
        let icon = chipConfig?.icon;
        const valueIcons = FACET_VALUE_ICONS[facetKey];
        if (valueIcons && valueIcons[value]) {
            icon = valueIcons[value];
        }

        chips.push({
          facetKey,
          value,
          label: getFacetLabel(facetKey, value, this.facetLabels),
          facetTitle: config ? this.translation.translate(config.translationKey) : facetKey,
          cssClass: chipConfig?.cssClass,
          icon: icon, // Use determined icon
          showLabel: chipConfig?.showLabel !== false // Keep label visibility as configured
        });
      });
    });

    const orderIndex = (key: string) => {
      const index = this.facetConfigs.findIndex(config => config.key === key);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    chips.sort((a, b) => {
      const diff = orderIndex(a.facetKey) - orderIndex(b.facetKey);
      if (diff !== 0) {
        return diff;
      }
      return a.label.localeCompare(b.label, locale);
    });

    return chips;
  }

  get isEnglish(): boolean {
    return this.translation.getLanguage() === 'en';
  }

  get statsLabel(): string {
    return this.translation.translate('resultsCount', {
      count: this.formatNumber(this.total)
    });
  }

  get processingTimeLabel(): string {
    if (this.processingTime == null) {
      return '';
    }
    return this.translation.translate('processingTime', {
      ms: this.processingTime
    });
  }

  get showMainContent(): boolean {
    return this.isUiExpanded;
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.page = 0;
    this.refreshUiExpansion();
    
    // Suggestion logic
    // Extract everything after the last separator (space, comma, etc) to find the current term
    const matches = value.match(/([^,;:/\\|\s]+)$/);
    const termToSearch = matches ? matches[1] : '';

    if (termToSearch.length >= 2) {
        this.searchService.getSuggestions(termToSearch).subscribe(suggestions => {
            this.locationSuggestions = suggestions.map(term => ({
                id: term,
                label: term,
                type: 'term'
            }));
        });
    } else {
        this.locationSuggestions = [];
    }
    
    if (this.useQuickResultsDropdown) {
      this.pendingQueryStatePush = false;
      this.scheduleQuickResults();
      return;
    }

    this.pendingQueryStatePush = true;
    this.updateUrlState({ replace: true });
    this.scheduleSearch();
  }

  onClearSearch(): void {
    this.query = '';
    this.page = 0;
    this.refreshUiExpansion();
    if (this.useQuickResultsDropdown) {
      this.pendingQueryStatePush = false;
      this.clearQuickResultsState();
      return;
    }

    this.pendingQueryStatePush = false;
    this.updateUrlState();
    this.scheduleSearch(false);
  }

  onResultClicked(event: { id: string; typeId?: string; position: number }): void {
    this.searchService.trackClick(this.query, event.id, event.typeId, event.position);
  }

  onTrendingTermSelected(term: string): void {
    this.query = term;
    this.page = 0;

    if (this.useQuickResultsDropdown) {
      this.navigateToFullSearch();
      return;
    }

    this.pendingQueryStatePush = true;
    this.updateUrlState({ replace: true });
    this.scheduleSearch();
  }

  onQuickResultSelected(result: QuickResult): void {
    if (!this.useQuickResultsDropdown) {
      return;
    }

    this.searchService.trackClick(this.query, result.id, undefined, undefined);

    if (result.link) {
      if (result.openInNewTab) {
        const newWindow = window.open(result.link, '_blank', 'noopener');
        if (newWindow) {
          newWindow.opener = null;
        }
      } else {
        window.location.href = result.link;
      }
      return;
    }

    this.navigateToFullSearch();
  }

  onSearchSubmit(): void {
    // triggerEmbeddedChat disabled for now
    // this.triggerEmbeddedChat();

    if (!this.useQuickResultsDropdown) {
      return;
    }

    this.navigateToFullSearch();
  }

  private triggerEmbeddedChat(): void {
    const trimmed = this.query.trim();
    if (!trimmed) {
      return;
    }
    this.searchSubmitted = true;
    this.callOnSearchClick(trimmed, 0);
  }

  private callOnSearchClick(query: string, attempt: number): void {
    const maxAttempts = 10;
    const delayMs = 500;

    if (typeof (window as any).ROEPAS_CHAT.onSearchClick === 'function') {
      console.log('[WiseVoice] Calling onSearchClick with query:', query);
      try {
        (window as any).ROEPAS_CHAT.onSearchClick(query);
      } catch (e) {
        console.error('[WiseVoice] Failed to trigger embedded chat:', e);
      }
      return;
    }

    if (attempt < maxAttempts) {
      console.log(`[WiseVoice] window.ROEPAS_CHAT.onSearchClick not yet available, retrying in ${delayMs}ms (attempt ${attempt + 1}/${maxAttempts})`);
      window.setTimeout(() => this.callOnSearchClick(query, attempt + 1), delayMs);
    } else {
      console.warn('[WiseVoice] window.ROEPAS_CHAT.onSearchClick not available after all retries');
    }
  }

  onFacetToggle(facetKey: string, value: string): void {
    this.toggleFacetValue(facetKey, value);
  }

  onRemoveFilter(event: { facetKey: string; value: string }): void {
    this.toggleFacetValue(event.facetKey, event.value);
  }

  onClearFilters(): void {
    // Reset to default filters from widget config, not empty object
    // This preserves system-configured hidden filters
    this.filters = this.widgetConfig.defaultFilter 
      ? JSON.parse(JSON.stringify(this.widgetConfig.defaultFilter)) 
      : {};
    this.page = 0;
    this.refreshUiExpansion();
    this.updateUrlState();
    this.loadResults();
  }

  toggleFiltersPanel(): void {
    if (this.hideFacets) {
      return;
    }
    this.showFiltersPanel = !this.showFiltersPanel;
  }

  onToggleFilters(): void {
    if (this.hideFacets) {
      return;
    }
    this.showFiltersPanel = !this.showFiltersPanel;
  }

  onMobileSortChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.onSortChange(select.value);
  }

  onPageChange(nextPage: number): void {
    if (nextPage === this.page) {
      return;
    }
    this.page = nextPage;
    this.updateUrlState();
    this.loadResults();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private toggleFacetValue(facetKey: string, rawValue: string): void {
    const value = String(rawValue);
    const nextFilters: FilterMap = {};
    Object.entries(this.filters).forEach(([key, values]) => {
      nextFilters[key] = [...values];
    });

    const target = nextFilters[facetKey] ? [...nextFilters[facetKey]] : [];
    const index = target.indexOf(value);

    if (index >= 0) {
      target.splice(index, 1);
    } else {
      target.push(value);
    }

    if (target.length) {
      nextFilters[facetKey] = target;
    } else {
      delete nextFilters[facetKey];
    }

    this.filters = nextFilters;
    this.page = 0;
    this.refreshUiExpansion();
    this.updateUrlState();
    this.loadResults();
  }

  private scheduleSearch(debounce = true): void {
    if (this.searchDebounceId !== null) {
      window.clearTimeout(this.searchDebounceId);
    }

    if (debounce) {
      this.searchDebounceId = window.setTimeout(() => {
        this.loadResults();
      }, 300);
    } else {
      this.loadResults();
    }
  }

  private scheduleQuickResults(debounce = true): void {
    if (!this.useQuickResultsDropdown) {
      return;
    }

    if (this.quickResultDebounceId !== null) {
      window.clearTimeout(this.quickResultDebounceId);
    }

    if (debounce) {
      this.quickResultDebounceId = window.setTimeout(() => {
        this.loadQuickResults();
      }, 200);
    } else {
      this.loadQuickResults();
    }
  }

  private loadQuickResults(): void {
    if (!this.useQuickResultsDropdown) {
      return;
    }

    if (this.quickResultDebounceId !== null) {
      window.clearTimeout(this.quickResultDebounceId);
      this.quickResultDebounceId = null;
    }

    const trimmedQuery = this.query.trim();
    if (trimmedQuery.length < 2) {
      this.clearQuickResultsState();
      return;
    }

    this.currentQuickResultRequest?.unsubscribe();
    this.quickResultsLoading = true;
    this.quickResults = [];

    this.currentQuickResultRequest = this.searchService
      .search(trimmedQuery, 0, this.filters, { rows: 5 })
      .subscribe({
        next: response => this.handleQuickResultsResponse(response),
        error: error => {
          console.warn('Quick search failed:', error);
          this.quickResults = [];
          this.quickResultsLoading = false;
        },
        complete: () => {
          this.quickResultsLoading = false;
        }
      });
  }

  private handleQuickResultsResponse(response: SearchResponse): void {
    const results = Array.isArray(response.results)
      ? this.sanitizeResults(response.results)
      : [];
    this.quickResults = results
      .map(item => this.toQuickResult(item))
      .filter((item): item is QuickResult => Boolean(item));
    
    // Sync top bar search with updated quickResults
    this.updateTopBarSearch();
  }

  private clearQuickResultsState(): void {
    if (this.quickResultDebounceId !== null) {
      window.clearTimeout(this.quickResultDebounceId);
      this.quickResultDebounceId = null;
    }
    this.currentQuickResultRequest?.unsubscribe();
    this.currentQuickResultRequest = undefined;
    this.quickResults = [];
    this.quickResultsLoading = false;
    
    // Sync top bar search
    this.updateTopBarSearch();
  }

  private navigateToFullSearch(): void {
    const targetUrl = this.buildFullSearchNavigationUrl();
    if (!targetUrl) {
      return;
    }

    window.location.href = targetUrl;
  }

  private updateViewportMode(): void {
    if (typeof window === 'undefined') {
      this.isMobileView = false;
      this.showFiltersPanel = !this.hideFacets;
      return;
    }

    const mobile = window.innerWidth <= 768;
    if (mobile !== this.isMobileView) {
      this.isMobileView = mobile;
      this.showFiltersPanel = this.hideFacets ? false : !mobile;
      return;
    }

    if (!mobile && !this.hideFacets) {
      this.showFiltersPanel = true;
    }
  }

  private buildFullSearchNavigationUrl(): string | null {
    const base = this.fullSearchUrl ? this.fullSearchUrl.trim() : '';
    if (!base) {
      return null;
    }

    const hash = this.buildCondensedQueryHash();
    if (!hash) {
      return base;
    }

    if (!base.includes('#')) {
      return `${base}#${hash}`;
    }

    const [path, existingHash = ''] = base.split('#', 2);
    const normalizedExisting = existingHash.endsWith('&') || !existingHash
      ? existingHash
      : `${existingHash}&`;
    return `${path}#${normalizedExisting}${hash}`;
  }

  private buildCondensedQueryHash(): string {
    const params = new URLSearchParams();
    const trimmed = this.query.trim();
    if (trimmed) {
      params.set('q', trimmed);
    }
    return params.toString();
  }

  private toQuickResult(item: SearchResult): QuickResult | null {
    if (!item || typeof item.id !== 'string') {
      return null;
    }

    const label = typeof item.name === 'string' ? item.name.trim() : '';
    if (!label) {
      return null;
    }

    const rawDescription = typeof item.description === 'string' ? item.description : '';
    const normalizedDescription = this.truncateText(decodeHtmlEntities(rawDescription));
    const resolvedLink = resolveResultLink(item);

    return {
      id: item.id,
      label,
      description: normalizedDescription,
      link: resolvedLink?.href || undefined,
      openInNewTab: resolvedLink?.target === '_blank'
    };
  }

  private truncateText(value: string, limit = 140): string | undefined {
    if (!value) {
      return undefined;
    }

    const normalized = value.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return undefined;
    }

    if (normalized.length <= limit) {
      return normalized;
    }

    const sliceEnd = Math.max(0, limit - 3);
    return `${normalized.slice(0, sliceEnd).trimEnd()}...`;
  }

  onSortChange(sort: string): void {
    this.currentSort = this.normalizeSort(sort);
    this.page = 0;
    this.scheduleSearch(false);
  }

  private loadResults(): void {
    this.currentRequest?.unsubscribe();
    if (this.searchDebounceId !== null) {
      window.clearTimeout(this.searchDebounceId);
      this.searchDebounceId = null;
    }

    if (!this.isUiExpanded) {
      this.pendingQueryStatePush = false;
      return;
    }

    if (this.pendingQueryStatePush) {
      this.updateUrlState({ forcePush: true });
      this.pendingQueryStatePush = false;
    }

    this.loading = true;
    this.error = null;

    this.currentRequest = this.searchService
      .search(this.query, this.page, this.filters, { sort: this.currentSort })
      .subscribe({
        next: response => this.handleSearchResponse(response),
        error: error => this.handleSearchError(error),
        complete: () => {
          this.loading = false;
        }
      });
  }

  private handleSearchResponse(response: SearchResponse): void {
    this.results = Array.isArray(response.results) ? this.sanitizeResults(response.results) : [];
    this.facets = response.facets ?? {};
    this.total = typeof response.total === 'number' ? response.total : 0;
    this.onlineCount = typeof response.meta?.onlineCount === 'number' ? response.meta.onlineCount : 0;
    this.processingTime = typeof response.qtime === 'number' ? response.qtime : null;
    this.applyFacetLabels(response.meta?.facetLabels);
  }

  private handleSearchError(error: unknown): void {
    this.loading = false;
    this.error = error instanceof Error ? error.message : this.translation.translate('errorLoading');
    this.results = [];
    this.facets = {};
    this.total = 0;
    this.onlineCount = 0;
    this.processingTime = null;
  }

  private handlePopState(_event: PopStateEvent): void {
    this.restoreStateFromUrl();
    if (this.useQuickResultsDropdown) {
      if (this.query.trim().length >= 2) {
        this.scheduleQuickResults(false);
      } else {
        this.clearQuickResultsState();
      }
    } else {
      this.loadResults();
    }
  }

  private restoreStateFromUrl(initial = false): void {
    this.isRestoringState = true;
    try {
      const hash = window.location.hash.replace(/^#/, '');
      const defaultSort = this.getDefaultSort();

      if (!hash) {
        this.query = '';
        this.page = 0;
        this.filters = this.widgetConfig.defaultFilter ? JSON.parse(JSON.stringify(this.widgetConfig.defaultFilter)) : {};
        this.currentSort = defaultSort;
      } else {
        const params = new URLSearchParams(hash);

        const queryParam = params.get('q');
        this.query = queryParam ?? '';

        const pageParam = params.get('page');
        const parsedPage = pageParam ? Number.parseInt(pageParam, 10) : 0;
        this.page = Number.isFinite(parsedPage) && parsedPage >= 0 ? parsedPage : 0;

        const filters: FilterMap = {};
        params.forEach((value, key) => {
          if (!key.startsWith(this.filterParamPrefix)) {
            return;
          }
          const facetKey = key.slice(this.filterParamPrefix.length);
          if (!facetKey) {
            return;
          }
          if (!filters[facetKey]) {
            filters[facetKey] = [];
          }
          filters[facetKey].push(value);
        });
        Object.keys(filters).forEach(key => {
          const uniqueValues = Array.from(new Set(filters[key] ?? []));
          filters[key] = uniqueValues;
        });
        this.filters = filters;

        const sortParam = params.get('sort');
        this.currentSort = this.normalizeSort(sortParam ?? defaultSort);
      }

      this.refreshUiExpansion();

      this.lastSerializedState = this.serializeState();
      this.pendingQueryStatePush = false;

      if (initial) {
        const baseUrl = `${window.location.pathname}${window.location.search}`;
        const stateUrl = this.lastSerializedState ? `${baseUrl}#${this.lastSerializedState}` : baseUrl;
        history.replaceState(this.buildHistoryState(), '', stateUrl);
      }
    } finally {
      this.isRestoringState = false;
    }
  }

  private updateUrlState(options: { replace?: boolean; forcePush?: boolean } = {}): void {
    if (this.useQuickResultsDropdown) {
      return;
    }

    if (this.isRestoringState) {
      return;
    }

    const serialized = this.serializeState();
    if (!options.forcePush && serialized === this.lastSerializedState) {
      return;
    }

    const baseUrl = `${window.location.pathname}${window.location.search}`;
    const nextUrl = serialized ? `${baseUrl}#${serialized}` : baseUrl;
    const state = this.buildHistoryState();

    if (options.replace) {
      history.replaceState(state, '', nextUrl);
    } else {
      history.pushState(state, '', nextUrl);
    }

    this.lastSerializedState = serialized;
  }

  private serializeState(): string {
    const params = new URLSearchParams();
    const defaultSort = this.getDefaultSort();

    const trimmedQuery = this.query.trim();
    if (trimmedQuery) {
      params.set('q', trimmedQuery);
    }

    if (this.page > 0) {
      params.set('page', String(this.page));
    }

    if (this.currentSort !== defaultSort) {
      params.set('sort', this.currentSort);
    }

    Object.entries(this.filters)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([facetKey, values]) => {
      if (!Array.isArray(values)) {
        return;
      }
      values
        .map(value => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .forEach(value => {
          params.append(`${this.filterParamPrefix}${facetKey}`, value);
        });
    });

    return params.toString();
  }

  private buildHistoryState(): unknown {
    return {
      query: this.query,
      page: this.page,
      sort: this.currentSort,
      filters: JSON.parse(JSON.stringify(this.filters))
    };
  }

  private getDefaultSort(): string {
    return this.normalizeSort(this.widgetConfig.defaultSort);
  }

  private normalizeSort(sort: string | null | undefined): string {
    if (typeof sort === 'string') {
      const trimmed = sort.trim();
      if (this.supportedSortOptions.has(trimmed)) {
        return trimmed;
      }
    }

    return 'score desc';
  }

  private getTypeCount(kind: 'procedure' | 'institution' | 'informativePage'): number {
    const typeBuckets = this.facets['typeId'] || {};
    const typeLabels = this.facetLabels.types || {};
    let totalCount = 0;

    Object.entries(typeBuckets).forEach(([rawKey, rawCount]) => {
      const count = Number(rawCount) || 0;
      if (count <= 0) {
        return;
      }

      const normalizedKey = rawKey.trim().toLowerCase();
      const normalizedLabel = String(typeLabels[rawKey] || rawKey).trim().toLowerCase();

      if (kind === 'procedure' && (normalizedKey === '1' || normalizedLabel.includes('procedur') || normalizedLabel.includes('procedure'))) {
        totalCount += count;
      }

      if (kind === 'institution' && (normalizedKey === '2' || normalizedLabel.includes('institu') || normalizedLabel.includes('institution'))) {
        totalCount += count;
      }

      if (kind === 'informativePage' && (normalizedKey === '3' || normalizedLabel.includes('informativ') || normalizedLabel.includes('informative'))) {
        totalCount += count;
      }
    });

    if (totalCount > 0) {
      return totalCount;
    }

    const selectedTypes = this.filters['typeId'] || [];
    if (selectedTypes.length === 1) {
      const selectedType = selectedTypes[0];
      if (
        (kind === 'procedure' && selectedType === '1') ||
        (kind === 'institution' && selectedType === '2') ||
        (kind === 'informativePage' && selectedType === '3')
      ) {
        return this.total;
      }
    }

    return 0;
  }

  private formatSummaryList(parts: string[]): string {
    if (!parts.length) {
      return '';
    }

    if (parts.length === 1) {
      return parts[0];
    }

    if (parts.length === 2) {
      return `${parts[0]} ${this.translation.translate('summaryAnd')} ${parts[1]}`;
    }

    return `${parts.slice(0, -1).join(', ')} ${this.translation.translate('summaryAnd')} ${parts[parts.length - 1]}`;
  }

  private sanitizeResults(results: SearchResult[]): SearchResult[] {
    const decodeOptional = (value?: string | null): string | undefined => {
      if (value == null) {
        return undefined;
      }
      const decoded = decodeHtmlEntities(value);
      return decoded;
    };

    return results.map(result => ({
      ...result,
      name: decodeHtmlEntities(result.name),
      description: decodeHtmlEntities(result.description),
      categoryLabel: decodeOptional(result.categoryLabel),
      domainLabel: decodeOptional(result.domainLabel),
      typeLabel: decodeOptional(result.typeLabel),
      availabilityTypeLabel: decodeOptional(result.availabilityTypeLabel),
      countyLabel: decodeOptional(result.countyLabel),
      cityLabel: decodeOptional(result.cityLabel),
      institutionName: decodeOptional(result.institutionName),
      annexes: this.sanitizeStringArray(result.annexes),
      areas: this.sanitizeStringArray(result.areas),
      targetAudience: this.sanitizeStringArray(result.targetAudience)
    }));
  }

  private sanitizeStringArray(values?: string[] | null): string[] | undefined {
    if (!Array.isArray(values)) {
      return values ?? undefined;
    }
    return values
      .map(item => decodeHtmlEntities(item))
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }

  private sanitizeLabelRecord(record?: Record<string, string>): Record<string, string> {
    if (!record) {
      return {};
    }
    const entries = Object.entries(record).map(([key, value]) => [key, decodeHtmlEntities(value)]);
    return Object.fromEntries(entries);
  }

  private applyFacetLabels(partial?: Partial<FacetLabels>): void {
    if (!partial) {
      return;
    }

    this.facetLabels = {
      domains: { ...this.facetLabels.domains, ...this.sanitizeLabelRecord(partial.domains) },
      categories: { ...this.facetLabels.categories, ...this.sanitizeLabelRecord(partial.categories) },
      types: { ...this.facetLabels.types, ...this.sanitizeLabelRecord(partial.types) },
      availabilityTypes: {
        ...this.facetLabels.availabilityTypes,
        ...this.sanitizeLabelRecord(partial.availabilityTypes)
      },
      counties: { ...this.facetLabels.counties, ...this.sanitizeLabelRecord(partial.counties) },
      cities: { ...this.facetLabels.cities, ...this.sanitizeLabelRecord(partial.cities) },
      annexes: { ...this.facetLabels.annexes, ...this.sanitizeLabelRecord(partial.annexes) },
      areas: { ...this.facetLabels.areas, ...this.sanitizeLabelRecord(partial.areas) },
      targetAudiences: {
        ...this.facetLabels.targetAudiences,
        ...this.sanitizeLabelRecord(partial.targetAudiences)
      }
    };

    this.rebuildLocationSuggestions();
  }

  private refreshUiExpansion(): void {
    if (this.widgetConfig.showFullUiOnLoad) {
      this.isUiExpanded = true;
      return;
    }

    if (this.useQuickResultsDropdown) {
      if (this.isUiExpanded) {
        this.isUiExpanded = false;
        this.resetResultsState();
      }
      return;
    }

    const hasQuery = this.query.trim().length > 0;
    const hasFilters = Object.values(this.filters).some(values => Array.isArray(values) && values.length > 0);
    const shouldExpand = hasQuery || hasFilters;

    if (!shouldExpand && this.isUiExpanded) {
      this.resetResultsState();
    }

    this.isUiExpanded = shouldExpand;
  }

  private resetResultsState(): void {
    this.results = [];
    this.facets = {};
    this.total = 0;
    this.onlineCount = 0;
    this.processingTime = null;
    this.loading = false;
    this.error = null;
  }

  private rebuildLocationSuggestions(): void {
    const unique = new Map<string, LocationSuggestionDetail>();

    const addSuggestion = (id: string, label: string, type: LocationSuggestionDetail['type']) => {
      if (!label) {
        return;
      }
      const tokens = tokenizeText(label);
      if (!tokens.length) {
        return;
      }
      const key = `${type}:${tokens.join(' ')}`;
      if (unique.has(key)) {
        return;
      }
      unique.set(key, {
        id,
        label,
        type,
        normalizedTokens: tokens
      });
    };

    Object.entries(this.facetLabels.counties || {}).forEach(([id, label]) => {
      addSuggestion(id, label, 'county');
    });

    Object.entries(this.facetLabels.cities || {}).forEach(([id, label]) => {
      addSuggestion(id, label, 'city');
    });

    const locale = this.translation.getLanguage() === 'ro' ? 'ro-RO' : 'en-US';
    // Removed location suggestion generation here as we use Solr terms now
    // The details are kept if needed for normalization logic but we don't populate locationSuggestions initially
    this.locationSuggestionDetails = Array.from(unique.values()).sort((a, b) =>
      a.label.localeCompare(b.label, locale)
    );
  }

  private buildImplicitFiltersFromQuery(query: string): FilterMap {
    if (!query || !this.locationSuggestionDetails.length) {
      return {};
    }

    const queryTokens = tokenizeText(query);
    if (!queryTokens.length) {
      return {};
    }

    const filters: FilterMap = {};
    const usedFacetKeys = new Set<string>();

    for (const suggestion of this.locationSuggestionDetails) {
      const facetKey = suggestion.type === 'county' ? 'countyId' : 'cityId';
      if (usedFacetKeys.has(facetKey)) {
        continue;
      }

      if (!this.matchesTokenSequence(queryTokens, suggestion.normalizedTokens)) {
        continue;
      }

      filters[facetKey] = [suggestion.id];
      usedFacetKeys.add(facetKey);

      if (usedFacetKeys.size === 2) {
        break;
      }
    }

    return filters;
  }

  private matchesTokenSequence(queryTokens: string[], candidateTokens: string[]): boolean {
    if (!candidateTokens.length) {
      return false;
    }

    if (candidateTokens.length === 1) {
      return queryTokens.includes(candidateTokens[0]);
    }

    for (let start = 0; start <= queryTokens.length - candidateTokens.length; start += 1) {
      let matches = true;
      for (let offset = 0; offset < candidateTokens.length; offset += 1) {
        if (queryTokens[start + offset] !== candidateTokens[offset]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return true;
      }
    }

    return false;
  }

  private mergeFilters(...maps: FilterMap[]): FilterMap {
    const result: FilterMap = {};

    maps.forEach(map => {
      Object.entries(map || {}).forEach(([facetKey, values]) => {
        if (!Array.isArray(values) || !values.length) {
          return;
        }

        const bucket = result[facetKey] ?? (result[facetKey] = []);
        values.forEach(value => {
          if (!bucket.includes(value)) {
            bucket.push(value);
          }
        });
      });
    });

    return result;
  }

  translate(key: string): string {
    return this.translation.translate(key);
  }

  formatNumber(value: number): string {
    const locale = this.translation.getLanguage() === 'ro' ? 'ro-RO' : 'en-US';
    try {
      return Number(value || 0).toLocaleString(locale);
    } catch {
      return String(value);
    }
  }
}
